import { AssetPermission, Role } from '@gm-commercial/profile-model';
import type { ComponentType, lazy } from 'react';

import {
  CHARGE_STATIONS_FLAG,
  FLEET_MAP_FLAG,
  GEOFENCES_FLAG,
} from '~/common/models/featureFlags.model';
import { RouteName } from '~/common/models/route.model';
import { lazyWithReload } from '~/common/utils/lazyWithReload';

/**
 * Lazily loads a map view and injects the `geofence` reducer as part of the same
 * dynamic import chunk. The geofence slice pulls `@brightdrop/map-ui` +
 * `mapbox-gl`, so it is deliberately kept out of the eager store and mounted
 * only when a map route is actually visited.
 *
 * `~/app/store` is imported dynamically (not statically) so this module — which
 * lives in the eagerly-evaluated route-config graph — does not create a circular
 * import with the store/reducers.
 */
/**
 * Injects the `geofence` reducer, then resolves the view module.
 *
 * The `geofence` reducer must be mounted before the view renders so its
 * selectors/thunks see a defined slice, so we `await` the store import and call
 * `injectReducer` *before* invoking `factory()` to load the view.
 *
 * @param factory - Dynamic import of the map view module to render.
 * @returns The resolved module, after the reducer has been injected.
 */
export const loadMapViewWithGeofenceReducer = async <P extends object>(
  factory: () => Promise<{ default: ComponentType<P> }>
): Promise<{ default: ComponentType<P> }> => {
  const [{ injectReducer }, { geofenceReducer }] = await Promise.all([
    import('~/app/store'),
    import('~/store/geofence/geofenceSlice'),
  ]);
  injectReducer('geofence', geofenceReducer);
  return factory();
};

const lazyMapView = <P extends object>(
  factory: () => Promise<{ default: ComponentType<P> }>
): ReturnType<typeof lazy<ComponentType<P>>> =>
  lazyWithReload(() => loadMapViewWithGeofenceReducer(factory));

const VehiclesMapView = lazyMapView(
  () => import('~/routes/organizations/vehicles/map/views/VehiclesMapView')
);
const VehicleTripsMapView = lazyMapView(
  () => import('~/routes/organizations/vehicles/map/views/VehicleTripsMapView')
);
const GeofencesMapView = lazyMapView(
  () => import('~/routes/organizations/vehicles/map/views/GeofencesMapView')
);
const CreateGeofenceMapView = lazyMapView(
  () =>
    import('~/routes/organizations/vehicles/map/views/CreateGeofenceMapView')
);
const EditGeofenceMapView = lazyMapView(
  () => import('~/routes/organizations/vehicles/map/views/EditGeofenceMapView')
);

const ORGANIZATIONS_MAP_ROUTES = {
  [RouteName.ORGANIZATIONS_VEHICLES_MAP]: {
    name: RouteName.ORGANIZATIONS_VEHICLES_MAP,
    path: '/organizations/:organizationsId/vehicles/map',
    authorizedPerms: [AssetPermission.READ],
    authorizedRoles: [
      Role.SYSTEM_MANAGER,
      Role.ORG_MANAGER,
      Role.FLEET_MANAGER,
    ],
    element: VehiclesMapView,
    requiredFeatureFlags: [FLEET_MAP_FLAG],
  },
  [RouteName.ORGANIZATIONS_MAP_VEHICLE_DETAILS_OVERVIEW]: {
    name: RouteName.ORGANIZATIONS_MAP_VEHICLE_DETAILS_OVERVIEW,
    path: '/organizations/:organizationsId/vehicles/map/:assetsId',
    authorizedPerms: [AssetPermission.READ],
    authorizedRoles: [
      Role.SYSTEM_MANAGER,
      Role.ORG_MANAGER,
      Role.FLEET_MANAGER,
    ],
    element: VehiclesMapView, // Reuse VehiclesMapView to avoid map remounting
    requiredFeatureFlags: [FLEET_MAP_FLAG],
  },
  [RouteName.ORGANIZATIONS_MAP_VEHICLE_DETAILS_TRIPS]: {
    name: RouteName.ORGANIZATIONS_MAP_VEHICLE_DETAILS_TRIPS,
    path: '/organizations/:organizationsId/vehicles/map/:assetsId/trips/:tripId?',
    authorizedPerms: [AssetPermission.READ],
    authorizedRoles: [
      Role.SYSTEM_MANAGER,
      Role.ORG_MANAGER,
      Role.FLEET_MANAGER,
    ],
    element: VehicleTripsMapView,
    requiredFeatureFlags: [FLEET_MAP_FLAG],
  },
  [RouteName.ORGANIZATIONS_MAP_GEOFENCES]: {
    name: RouteName.ORGANIZATIONS_MAP_GEOFENCES,
    path: '/organizations/:organizationsId/vehicles/map/geofences',
    authorizedPerms: [AssetPermission.READ],
    authorizedRoles: [
      Role.SYSTEM_MANAGER,
      Role.ORG_MANAGER,
      Role.FLEET_MANAGER,
    ],
    element: GeofencesMapView, // Reuse for list and details to avoid map remounting
    requiredFeatureFlags: [GEOFENCES_FLAG],
  },
  [RouteName.ORGANIZATIONS_MAP_GEOFENCES_CREATE]: {
    name: RouteName.ORGANIZATIONS_MAP_GEOFENCES_CREATE,
    path: '/organizations/:organizationsId/vehicles/map/geofences/create',
    authorizedPerms: [AssetPermission.READ],
    authorizedRoles: [
      Role.SYSTEM_MANAGER,
      Role.ORG_MANAGER,
      Role.FLEET_MANAGER,
    ],
    element: CreateGeofenceMapView,
    requiredFeatureFlags: [GEOFENCES_FLAG],
  },
  [RouteName.ORGANIZATIONS_MAP_GEOFENCES_EDIT]: {
    name: RouteName.ORGANIZATIONS_MAP_GEOFENCES_EDIT,
    path: '/organizations/:organizationsId/vehicles/map/geofences/:geofencesId/edit',
    authorizedPerms: [AssetPermission.READ],
    authorizedRoles: [
      Role.SYSTEM_MANAGER,
      Role.ORG_MANAGER,
      Role.FLEET_MANAGER,
    ],
    element: EditGeofenceMapView,
    requiredFeatureFlags: [GEOFENCES_FLAG],
  },
  [RouteName.ORGANIZATIONS_MAP_GEOFENCES_DETAILS]: {
    name: RouteName.ORGANIZATIONS_MAP_GEOFENCES_DETAILS,
    path: '/organizations/:organizationsId/vehicles/map/geofences/:geofencesId',
    authorizedPerms: [AssetPermission.READ],
    authorizedRoles: [
      Role.SYSTEM_MANAGER,
      Role.ORG_MANAGER,
      Role.FLEET_MANAGER,
    ],
    element: GeofencesMapView, // Reuse GeofencesMapView to avoid map remounting
    requiredFeatureFlags: [GEOFENCES_FLAG],
  },
  [RouteName.ORGANIZATIONS_MAP_CHARGING_STATIONS]: {
    name: RouteName.ORGANIZATIONS_MAP_CHARGING_STATIONS,
    path: '/organizations/:organizationsId/vehicles/map/charging-stations',
    authorizedPerms: [AssetPermission.READ],
    authorizedRoles: [
      Role.SYSTEM_MANAGER,
      Role.ORG_MANAGER,
      Role.FLEET_MANAGER,
    ],
    element: VehiclesMapView, // Reuse VehiclesMapView - it detects the route and shows charging stations
    requiredFeatureFlags: [CHARGE_STATIONS_FLAG],
  },
};

export default ORGANIZATIONS_MAP_ROUTES;
